import { useCallback, useEffect, useState } from "react";
import type {
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
} from "react-plaid-link";
import { usePlaidLink } from "react-plaid-link";
import {
  createPlaidLinkToken,
  logPlaidLinkEvent,
  useCreatePlaidLinkMutation,
  useReconnectPlaidLinkMutation,
} from "@/lib/data";
import {
  PLAID_LINK_RECONNECT_STORAGE_KEY,
  PLAID_LINK_TASK_STORAGE_KEY,
  PLAID_LINK_TOKEN_STORAGE_KEY,
} from "@/lib/session";

function clearStoredLinkSession() {
  localStorage.removeItem(PLAID_LINK_TOKEN_STORAGE_KEY);
  localStorage.removeItem(PLAID_LINK_TASK_STORAGE_KEY);
  localStorage.removeItem(PLAID_LINK_RECONNECT_STORAGE_KEY);
}

// Stores the value, or clears the key when this session has none.
function storeOrClear(key: string, value: string | undefined) {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * One Plaid Link session: fetches a link token, opens Link, and saves the
 * result. Links a new bank, or with `reconnectLinkId` repairs that one in
 * Link's update mode (same access token, so nothing to exchange).
 *
 * Banks that log in with OAuth leave the page and come back to /plaid-links,
 * so the token, the task and the bank being repaired are kept in localStorage
 * until Link finishes.
 */
export function usePlaidLinkSession({
  taskId,
  reconnectLinkId,
  onLinked,
  onExit: onLinkExit,
}: {
  taskId?: string;
  reconnectLinkId?: string;
  onLinked: () => Promise<void>;
  onExit?: () => void;
}) {
  const createPlaidLink = useCreatePlaidLinkMutation();
  const reconnectPlaidLink = useReconnectPlaidLinkMutation();
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the stored token belongs to the OAuth session in the URL; a token
  // fetched afterwards starts a new session and must not claim it.
  const [resumingOAuth, setResumingOAuth] = useState(() =>
    new URLSearchParams(window.location.search).has("oauth_state_id"),
  );
  const [linkToken, setLinkToken] = useState<string | null>(() =>
    resumingOAuth ? localStorage.getItem(PLAID_LINK_TOKEN_STORAGE_KEY) : null,
  );

  async function start() {
    setResumingOAuth(false);
    setStarting(true);
    setError(null);
    try {
      const { link_token } = await createPlaidLinkToken(reconnectLinkId);
      localStorage.setItem(PLAID_LINK_TOKEN_STORAGE_KEY, link_token);
      // Both keys are rewritten on every start. An applicant who left for an
      // OAuth bank and never came back leaves them behind, and a stale
      // reconnect id would make /plaid-links repair that old bank instead of
      // saving the new one.
      storeOrClear(PLAID_LINK_TASK_STORAGE_KEY, taskId);
      storeOrClear(PLAID_LINK_RECONNECT_STORAGE_KEY, reconnectLinkId);
      setLinkToken(link_token);
    } catch {
      setError("Unable to initialize bank connection. Please try again.");
    } finally {
      setStarting(false);
    }
  }

  const onSuccess = useCallback(
    async (
      publicToken: string | null,
      metadata: PlaidLinkOnSuccessMetadata,
    ) => {
      clearStoredLinkSession();
      setSaving(true);
      try {
        if (reconnectLinkId) {
          await reconnectPlaidLink.mutateAsync(reconnectLinkId);
        } else {
          // Link only withholds the token in flows that don't create an Item,
          // and linking a bank always creates one.
          if (!publicToken) throw new Error("Plaid Link returned no token");
          await createPlaidLink.mutateAsync({
            public_token: publicToken,
            metadata: metadata as unknown as Record<string, unknown>,
          });
        }
        await onLinked();
      } catch {
        setError(
          reconnectLinkId
            ? "We couldn't reconnect your bank. Please try again."
            : "Failed to link your bank account. Please try again.",
        );
      } finally {
        setSaving(false);
      }
    },
    [createPlaidLink, reconnectPlaidLink, reconnectLinkId, onLinked],
  );

  const onExit = useCallback(
    (err: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      clearStoredLinkSession();
      void logPlaidLinkEvent({
        name: "EXIT",
        timestamp: new Date().toISOString(),
        metadata: metadata as unknown as Record<string, unknown>,
        is_error: !!err,
      });
      if (err) {
        setError("Bank connection was not completed. Please try again.");
      }
      setLinkToken(null);
      onLinkExit?.();
    },
    [onLinkExit],
  );

  const onEvent = useCallback(
    (eventName: string, metadata: PlaidLinkOnEventMetadata) => {
      void logPlaidLinkEvent({
        name: eventName,
        timestamp: metadata.timestamp,
        metadata: metadata as unknown as Record<string, unknown>,
        is_error: eventName === "ERROR",
      });
    },
    [],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- onSuccess catches its own errors
    onSuccess,
    onExit,
    onEvent,
    ...(resumingOAuth ? { receivedRedirectUri: window.location.href } : {}),
  });

  useEffect(() => {
    if (ready && linkToken) {
      open();
    }
  }, [ready, linkToken, open]);

  return { start, starting, saving, error };
}
